// src/pages/production/ProductsTab.tsx
//
// Phase 2B Products (BOM) CRUD. A Product = a finished SKU built from a
// Recipe plus packaging items. Cost rolls up:
//   total = (recipe cost per yield-unit × unitsPerBatch) + sum(packaging cost)
// Margin computed from optional sellPrice.

import { useEffect, useMemo, useState } from 'react';
import { Plus, Pencil, Trash2, Search, Boxes, X, AlertCircle } from 'lucide-react';
import Modal from '../../components/Modal';
import {
  getItems, getRecipes, getProducts, saveProduct, deleteProduct, calcProductCost,
} from '../../services/productionStorage';
import type {
  ProductionItem, ProductionRecipe, ProductionProduct, ProductPackagingLine,
} from '../../types/production';

interface ProductsTabProps {
  slug: string;
}

const UNIT_OPTIONS = ['ea', 'lb', 'oz', 'kg', 'g', 'gal', 'qt', 'pt', 'cup', 'fl oz', 'ml', 'L'];
const INPUT_CLASS = 'w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-sm text-white placeholder-slate-500 focus:border-orange-500/50 focus:outline-none';

interface EditState {
  id?: string;
  name: string;
  sku: string;
  description: string;
  category: string;
  recipeId: string;
  unitsPerBatch: string;
  sellPrice: string;
  packaging: Array<{ itemId: string; qty: string; unit: string }>;
}

const EMPTY: EditState = {
  name: '', sku: '', description: '', category: '', recipeId: '',
  unitsPerBatch: '1', sellPrice: '', packaging: [],
};

export default function ProductsTab({ slug }: ProductsTabProps) {
  const [items, setItems] = useState<ProductionItem[]>([]);
  const [recipes, setRecipes] = useState<ProductionRecipe[]>([]);
  const [products, setProducts] = useState<ProductionProduct[]>([]);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<EditState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ProductionProduct | null>(null);

  useEffect(() => {
    setItems(getItems(slug));
    setRecipes(getRecipes(slug));
    setProducts(getProducts(slug));
  }, [slug]);

  const itemsById = useMemo(() => {
    const m: Record<string, ProductionItem> = {};
    for (const i of items) m[i.id] = i;
    return m;
  }, [items]);

  const recipesById = useMemo(() => {
    const m: Record<string, ProductionRecipe> = {};
    for (const r of recipes) m[r.id] = r;
    return m;
  }, [recipes]);

  const filtered = useMemo(() => {
    if (!search) return products;
    const q = search.toLowerCase();
    return products.filter((p) =>
      p.name.toLowerCase().includes(q) ||
      (p.sku || '').toLowerCase().includes(q) ||
      (p.description || '').toLowerCase().includes(q) ||
      (p.category || '').toLowerCase().includes(q),
    );
  }, [products, search]);

  const productsWithCost = useMemo(() => {
    return filtered.map((p) => {
      const cost = calcProductCost(p, recipesById, itemsById);
      const margin = p.sellPrice && p.sellPrice > 0
        ? ((p.sellPrice - cost.totalCost) / p.sellPrice) * 100
        : null;
      return { product: p, ...cost, margin };
    });
  }, [filtered, recipesById, itemsById]);

  function openNew() { setEditing({ ...EMPTY }); }
  function openEdit(p: ProductionProduct) {
    setEditing({
      id: p.id,
      name: p.name,
      sku: p.sku || '',
      description: p.description || '',
      category: p.category || '',
      recipeId: p.recipeId,
      unitsPerBatch: String(p.unitsPerBatch),
      sellPrice: p.sellPrice != null ? String(p.sellPrice) : '',
      packaging: p.packaging.map((x) => ({ itemId: x.itemId, qty: String(x.qty), unit: x.unit })),
    });
  }

  function addPackagingLine() {
    if (!editing) return;
    setEditing({ ...editing, packaging: [...editing.packaging, { itemId: '', qty: '1', unit: 'ea' }] });
  }
  function updatePackagingLine(idx: number, patch: Partial<EditState['packaging'][0]>) {
    if (!editing) return;
    const next = [...editing.packaging];
    next[idx] = { ...next[idx], ...patch };
    setEditing({ ...editing, packaging: next });
  }
  function removePackagingLine(idx: number) {
    if (!editing) return;
    setEditing({ ...editing, packaging: editing.packaging.filter((_, i) => i !== idx) });
  }

  function handleSave() {
    if (!editing) return;
    const name = editing.name.trim();
    if (!name || !editing.recipeId) return;

    const packaging: ProductPackagingLine[] = editing.packaging
      .filter((p) => p.itemId)
      .map((p) => ({
        itemId: p.itemId,
        qty: parseFloat(p.qty) || 0,
        unit: p.unit || 'ea',
      }));

    const unitsPerBatch = parseFloat(editing.unitsPerBatch);
    const sellPrice = editing.sellPrice.trim() ? parseFloat(editing.sellPrice) : undefined;

    saveProduct(slug, {
      id: editing.id,
      name,
      sku: editing.sku.trim() || undefined,
      description: editing.description.trim() || undefined,
      category: editing.category.trim() || undefined,
      recipeId: editing.recipeId,
      unitsPerBatch: Number.isFinite(unitsPerBatch) && unitsPerBatch > 0 ? unitsPerBatch : 1,
      packaging,
      sellPrice: Number.isFinite(sellPrice as number) ? sellPrice : undefined,
    });
    setProducts(getProducts(slug));
    setEditing(null);
  }

  function handleDelete() {
    if (!confirmDelete) return;
    deleteProduct(slug, confirmDelete.id);
    setProducts(getProducts(slug));
    setConfirmDelete(null);
  }

  // Live cost preview inside the modal
  const previewCost = useMemo(() => {
    if (!editing) return null;
    const fakeProduct: ProductionProduct = {
      id: editing.id || 'preview',
      name: editing.name || 'preview',
      recipeId: editing.recipeId,
      unitsPerBatch: parseFloat(editing.unitsPerBatch) || 1,
      packaging: editing.packaging
        .filter((p) => p.itemId)
        .map((p) => ({
          itemId: p.itemId,
          qty: parseFloat(p.qty) || 0,
          unit: p.unit || 'ea',
        })),
      createdAt: '', updatedAt: '',
    };
    return calcProductCost(fakeProduct, recipesById, itemsById);
  }, [editing, recipesById, itemsById]);

  return (
    <div>
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-white">Products</h2>
          <p className="text-sm text-slate-400 mt-0.5">
            Finished SKUs — recipe + packaging. Cost rolls up automatically.
          </p>
        </div>
        <button
          onClick={openNew}
          disabled={recipes.length === 0}
          title={recipes.length === 0 ? 'Add a recipe first.' : ''}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-orange-500 hover:bg-orange-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium text-sm transition-colors"
        >
          <Plus size={14} />
          New product
        </button>
      </div>

      {recipes.length === 0 && (
        <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200 text-sm flex items-center gap-2">
          <AlertCircle size={14} />
          Add a recipe first — products are built from recipes.
        </div>
      )}

      <div className="mb-4 relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search products by name, SKU, description, or category…"
          className="w-full pl-9 pr-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-sm text-white placeholder-slate-500 focus:border-orange-500/50 focus:outline-none"
        />
      </div>

      {productsWithCost.length === 0 ? (
        <div className="text-center py-16 rounded-2xl bg-slate-800/30 border border-slate-700/40">
          <Boxes size={32} className="mx-auto text-slate-600 mb-3" />
          <p className="text-slate-400 text-sm">
            {products.length === 0 ? 'No products yet.' : 'No products match your search.'}
          </p>
        </div>
      ) : (
        <div className="rounded-2xl bg-slate-800/30 border border-slate-700/40 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 text-xs uppercase tracking-wider bg-slate-900/40">
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">SKU</th>
                <th className="px-4 py-3 font-medium">Recipe</th>
                <th className="px-4 py-3 font-medium text-right">Recipe cost</th>
                <th className="px-4 py-3 font-medium text-right">Pkg cost</th>
                <th className="px-4 py-3 font-medium text-right">Total</th>
                <th className="px-4 py-3 font-medium text-right">Sell</th>
                <th className="px-4 py-3 font-medium text-right">Margin</th>
                <th className="px-4 py-3 font-medium w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/40">
              {productsWithCost.map(({ product, totalCost, recipeCost, packagingCost, hasMissing, margin }) => {
                const recipe = recipesById[product.recipeId];
                return (
                  <tr key={product.id} className="hover:bg-slate-700/20">
                    <td className="px-4 py-3">
                      <div className="text-white font-medium flex items-center gap-2">
                        {product.name}
                        {hasMissing && (
                          <span title="Recipe or packaging item missing">
                            <AlertCircle size={12} className="text-amber-400" />
                          </span>
                        )}
                      </div>
                      {product.description && (
                        <div className="text-xs text-slate-500 mt-0.5">{product.description}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-400 font-mono text-xs">{product.sku || '—'}</td>
                    <td className="px-4 py-3 text-slate-300">
                      {recipe ? recipe.name : <span className="text-amber-400">missing</span>}
                      {recipe && (
                        <div className="text-xs text-slate-500">
                          × {product.unitsPerBatch} {recipe.yieldUnit}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-slate-200">${recipeCost.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-mono text-slate-200">${packagingCost.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-mono text-white">${totalCost.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-mono text-slate-300">
                      {product.sellPrice != null ? `$${product.sellPrice.toFixed(2)}` : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {margin == null ? (
                        <span className="text-slate-500">—</span>
                      ) : (
                        <span className={`font-mono ${
                          margin > 50 ? 'text-emerald-400' : margin > 30 ? 'text-orange-400' : 'text-red-400'
                        }`}>
                          {margin.toFixed(1)}%
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => openEdit(product)}
                          className="p-1.5 rounded text-slate-400 hover:text-blue-400 hover:bg-blue-500/10"
                          title="Edit"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => setConfirmDelete(product)}
                          className="p-1.5 rounded text-slate-400 hover:text-red-400 hover:bg-red-500/10"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <Modal onClose={() => setEditing(null)} panelClassName="max-w-3xl">
          <h3 className="text-lg font-semibold text-white mb-4">
            {editing.id ? 'Edit product' : 'New product'}
          </h3>

          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name">
                <input
                  autoFocus
                  type="text"
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className={INPUT_CLASS}
                />
              </Field>
              <Field label="SKU (optional)">
                <input
                  type="text"
                  value={editing.sku}
                  onChange={(e) => setEditing({ ...editing, sku: e.target.value })}
                  className={INPUT_CLASS}
                />
              </Field>
            </div>
            <Field label="Description">
              <input
                type="text"
                value={editing.description}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                className={INPUT_CLASS}
              />
            </Field>
            <div className="grid grid-cols-4 gap-3">
              <Field label="Category">
                <input
                  type="text"
                  value={editing.category}
                  onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                  className={INPUT_CLASS}
                />
              </Field>
              <Field label="Recipe">
                <select
                  value={editing.recipeId}
                  onChange={(e) => setEditing({ ...editing, recipeId: e.target.value })}
                  className={INPUT_CLASS}
                >
                  <option value="">— Select recipe —</option>
                  {recipes.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="Units per batch">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editing.unitsPerBatch}
                  onChange={(e) => setEditing({ ...editing, unitsPerBatch: e.target.value })}
                  className={`${INPUT_CLASS} font-mono`}
                />
              </Field>
              <Field label="Sell price (optional)">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editing.sellPrice}
                  onChange={(e) => setEditing({ ...editing, sellPrice: e.target.value })}
                  placeholder="—"
                  className={`${INPUT_CLASS} font-mono`}
                />
              </Field>
            </div>

            {/* Packaging */}
            <div className="pt-2">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Packaging</span>
                <button
                  type="button"
                  onClick={addPackagingLine}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-slate-700 hover:bg-slate-600 text-white"
                >
                  <Plus size={12} /> Add line
                </button>
              </div>
              {editing.packaging.length === 0 ? (
                <div className="text-xs text-slate-500 italic py-2">
                  No packaging yet — jars, lids, labels go here.
                </div>
              ) : (
                <div className="space-y-2">
                  {editing.packaging.map((p, idx) => {
                    const item = itemsById[p.itemId];
                    return (
                      <div key={idx} className="grid grid-cols-[1fr_80px_80px_auto] gap-2 items-center">
                        <select
                          value={p.itemId}
                          onChange={(e) => updatePackagingLine(idx, { itemId: e.target.value })}
                          className={INPUT_CLASS}
                        >
                          <option value="">— Select packaging item —</option>
                          {items.map((it) => (
                            <option key={it.id} value={it.id}>
                              {it.name} (${it.costPerUnit.toFixed(4)}/{it.unit})
                            </option>
                          ))}
                        </select>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={p.qty}
                          onChange={(e) => updatePackagingLine(idx, { qty: e.target.value })}
                          className={`${INPUT_CLASS} font-mono`}
                        />
                        <select
                          value={p.unit}
                          onChange={(e) => updatePackagingLine(idx, { unit: e.target.value })}
                          className={INPUT_CLASS}
                        >
                          {UNIT_OPTIONS.map((u) => <option key={u} value={u}>{u}</option>)}
                        </select>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono text-slate-400 w-16 text-right">
                            {item ? `$${((parseFloat(p.qty) || 0) * item.costPerUnit).toFixed(2)}` : '—'}
                          </span>
                          <button
                            type="button"
                            onClick={() => removePackagingLine(idx)}
                            className="p-1.5 rounded text-slate-500 hover:text-red-400 hover:bg-red-500/10"
                            title="Remove line"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {previewCost && (
            <div className="mt-4 p-3 rounded-lg bg-slate-900/50 border border-slate-700/40 flex items-center justify-between text-sm">
              <div className="flex items-center gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Recipe</div>
                  <div className="font-mono text-white">${previewCost.recipeCost.toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Packaging</div>
                  <div className="font-mono text-white">${previewCost.packagingCost.toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Total / unit</div>
                  <div className="font-mono text-white">${previewCost.totalCost.toFixed(2)}</div>
                </div>
              </div>
              {previewCost.hasMissing && (
                <div className="text-xs text-amber-400 flex items-center gap-1">
                  <AlertCircle size={12} />
                  Recipe or item missing
                </div>
              )}
            </div>
          )}

          <div className="mt-6 flex justify-end gap-2">
            <button
              onClick={() => setEditing(null)}
              className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!editing.name.trim() || !editing.recipeId}
              className="px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-400 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium"
            >
              {editing.id ? 'Save changes' : 'Create product'}
            </button>
          </div>
        </Modal>
      )}

      {confirmDelete && (
        <Modal onClose={() => setConfirmDelete(null)} panelClassName="max-w-sm">
          <h3 className="text-lg font-semibold text-white mb-2">Delete product?</h3>
          <p className="text-sm text-slate-400 mb-6">
            Delete <span className="text-white font-medium">{confirmDelete.name}</span>?
            Forecasts referencing it will become unresolved.
          </p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setConfirmDelete(null)}
              className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-400 text-white text-sm font-medium"
            >
              Delete
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-400 mb-1">{label}</span>
      {children}
    </label>
  );
}
